import { z } from 'zod';

/* ---------- Helpers: option object & flexible parsers ---------- */
const optionObj = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});

// required: accept object/string/''/null → required → return object
const requiredOptionOneOfFlex = (allowed, msg = 'Please select an option') =>
  z
    .union([optionObj, z.string(), z.null(), z.literal('')])
    .refine((v) => v !== null && v !== '', { message: msg })
    .refine(
      (v) => {
        const val = typeof v === 'string' ? v : v?.value;
        return typeof val === 'string' && allowed.includes(val);
      },
      { message: 'Invalid option' }
    )
    .transform((v) => (typeof v === 'string' ? { label: v, value: v } : v));

// required (no whitelist): nationality — accept object/string/''/null → required → object
const requiredOptionAnyFlex = (msg = 'Please select an option') =>
  z
    .union([optionObj, z.string(), z.null(), z.literal('')])
    .refine((v) => v !== null && v !== '', { message: msg })
    .transform((v) => (typeof v === 'string' ? { label: v, value: v } : v));

// optional/conditional: accept object/string/''/null → ''/null → undefined, otherwise validate & object-ify
const optionalOptionOneOfFlex = (allowed) =>
  z
    .union([optionObj, z.string(), z.null(), z.literal('')])
    .transform((v) =>
      v === null || v === ''
        ? undefined
        : typeof v === 'string'
        ? { label: v, value: v }
        : v
    )
    .refine((v) => !v || allowed.includes(v.value), {
      message: 'Invalid option',
    });

/* ---------- Date helpers ---------- */
const isISODate = (s) => !Number.isNaN(Date.parse(s));
const within = (s, min, max) => isISODate(s) && s >= min && s <= max;

/* ---------- Email section ---------- */
export const emailSchema = z
  .object({
    email: z
      .string({ required_error: 'Email is required' })
      .email('Invalid email format'),
    repeat_email: z
      .string({ required_error: 'Repeat Email is required' })
      .email('Invalid email format'),
  })
  .superRefine((val, ctx) => {
    if (
      (val.email?.length ?? 0) > 0 &&
      (val.repeat_email?.length ?? 0) > 0 &&
      val.email !== val.repeat_email
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['repeat_email'],
        message: "Email doesn't match",
      });
    }
  });

/* ---------- Runner base (without category conditions) ---------- */
const rpt10Allowed = ['pen_a', 'pen_b', 'pen_c', 'pen_d', 'pen_e'];
const rpt21Allowed = [
  'competitive',
  'pen_a',
  'pen_b',
  'pen_c',
  'pen_d',
  'pen_e',
  'pen_f',
  'pen_g',
];

export const runnerBaseSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  contact: z
    .string()
    .min(5, 'Contact is required')
    .refine(
      (s) => /^[0-9+()\-\s]+$/.test(s),
      'Only digits and phone symbols are allowed'
    )
    .refine((s) => !s.trim().startsWith('-'), 'Cannot start with a minus sign')
    .refine(
      (s) => s.indexOf('+') <= 0,
      'Plus sign is allowed only at the beginning'
    )
    .refine(
      (s) => s.replace(/\D/g, '').length >= 7,
      'Phone number is too short'
    ),
  gender: requiredOptionOneOfFlex(['m', 'f']),
  nationality: requiredOptionAnyFlex(),
  id_type: requiredOptionOneOfFlex([
    'ktp',
    'paspor',
    'kitas',
    'kartu-pelajar',
    'sim',
  ]),
  id_number: z.string().min(1, 'ID Number is required'),

  brith_date: z
    .string()
    .refine((s) => within(s, '1900-01-01', '2099-12-31'), 'Invalid date'),

  blood_type: requiredOptionOneOfFlex([
    'a+',
    'a-',
    'b+',
    'b-',
    'ab+',
    'ab-',
    'o+',
    'o-',
    'none',
  ]),

  // Radio remains string
  org_kom: z
    .union([optionObj, z.string(), z.null(), z.literal('')])
    .transform((v) => (v && typeof v === 'object' ? v.value : v))
    .refine((v) => v !== null && v !== '', {
      message: 'Please select an option',
    })
    .refine((v) => v === 'yes' || v === 'no', {
      message: 'Please select an option',
    }),

  org_com: z.string().optional(),

  // ✅ Optional di level property, biar yang tidak tampil tidak memicu error
  rpt_10: optionalOptionOneOfFlex(rpt10Allowed).optional(),
  rpt_21: optionalOptionOneOfFlex(rpt21Allowed).optional(),

  exp_fin_time: z
    .object({
      hour: z.number().int().min(0).max(12),
      minute: z.number().int().min(0).max(60),
      second: z.number().int().min(0).max(60),
    })
    .superRefine((t, ctx) => {
      const total =
        (t.hour ?? 0) * 3600 + (t.minute ?? 0) * 60 + (t.second ?? 0);
      if (total < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Finish time must be at least 00:00:01',
          path: [],
        });
      }
    }),

  medical_note: z.string().max(2000, 'Max 2000 characters').optional(),
});

/* ---------- Emergency section ---------- */
export const emergencySchema = z.object({
  emergency_person: z.string().min(1, 'Name is required'),
  emergency_contact: z
    .string()
    .min(5, 'Contact is required')
    .refine(
      (s) => /^[0-9+()\-\s]+$/.test(s),
      'Only digits and phone symbols are allowed'
    )
    .refine((s) => !s.trim().startsWith('-'), 'Cannot start with a minus sign')
    .refine(
      (s) => s.indexOf('+') <= 0,
      'Plus sign is allowed only at the beginning'
    )
    .refine(
      (s) => s.replace(/\D/g, '').length >= 7,
      'Phone number is too short'
    ),
  emergency_relation: requiredOptionOneOfFlex([
    'keluarga',
    'ortu',
    'saudara',
    'sum_is',
    'guru',
    'teman',
  ]),
});

/* ---------- Full form + conditional rules ---------- */
export const registrationSchema = z
  .object({
    race_category: z.enum(['5k', '10k', '21k']),
  })
  .merge(emailSchema)
  .merge(runnerBaseSchema)
  .merge(emergencySchema)
  .superRefine((val, ctx) => {
    if (
      (val.email?.length ?? 0) > 0 &&
      (val.repeat_email?.length ?? 0) > 0 &&
      val.email !== val.repeat_email
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['repeat_email'],
        message: "Email doesn't match",
      });
    }

    // Conditional RPT
    if (val.race_category === '10k') {
      if (!val.rpt_10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rpt_10'],
          message: 'Please select an option',
        });
      }
    } else if (val.race_category === '21k') {
      if (!val.rpt_21) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rpt_21'],
          message: 'Please select an option',
        });
      }
    }

    // Running club name
    if (
      val.org_kom === 'yes' &&
      (!val.org_com || !String(val.org_com).trim())
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['org_com'],
        message: 'Please input the running club name',
      });
    }
  });
